<template>
  <div
    class="page-content-wrap service-form-wrap page-wrapper bg-gra-02 p-t-10 p-b-100 font-poppins"
  >
    <FileFullPreviewMode />
    <form @submit.prevent="onSubmit($event)">
      <div class="wrapper wrapper--wide">
        <div class="card card-12">
          <div class="card-body">
            <!-- Revise notice -->
            <div
              v-if="data.status === 'revise' && data.revise_comments"
              class="row"
            >
              <div class="col-12">
                <div
                  class="alert alert-warning alert-content"
                  v-html="data.revise_comments"
                ></div>
              </div>
            </div>

            <!-- Dynamic form -->
            <div class="row row-service-form btn-row row-space">
              <div
                v-for="(attribute, index) in formSchema"
                :key="index"
                :class="columnClass(attribute)"
                class="col-service-form"
              >
                <!-- Static content (kind: form_data) -->
                <template v-if="attribute.kind === 'form_data'">
                  <div class="input-group">
                    <label class="label">
                      <span :class="labelVisibilityClasses(attribute)">
                        <template v-if="attribute.type === 'tender_purpose'">
                          {{ lang(tender.purpose_label) }}
                        </template>
                        <template v-else>
                          {{ lang(attribute.name) }}
                        </template>
                      </span>
                    </label>

                    <!-- Special display-only attributes coming from tender object -->
                    <div
                      v-if="attribute.type === 'tender_purpose'"
                      class="info-value"
                    >
                      <div v-html="lang(tender.purpose_content)"></div>
                    </div>
                    <div
                      v-else-if="attribute.type === 'tender_description'"
                      class="info-value"
                    >
                      <div
                        class="alert alert-secondary tender-description"
                        v-html="lang(tender.description)"
                      ></div>
                    </div>
                    <div
                      v-else-if="attribute.type === 'minimum_fee'"
                      class="info-value"
                    >
                      <div>
                        <div class="tp-minfee-words text-muted text-left">
                          {{ tender.minimum_fee }} EUR
                        </div>
                        <div class="tp-minfee-words text-muted text-left">
                          {{ tender.minimum_fee_words }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-else-if="attribute.type === 'participation_fee'"
                      class="info-value"
                    >
                      <div>
                        <div class="tp-minfee-words text-muted text-left">
                          {{ tender.participation_fee }} EUR
                        </div>
                        <div class="tp-minfee-words text-muted text-left">
                          {{ tender.participation_fee_words }}
                        </div>
                      </div>
                    </div>

                    <!-- Regular static content from form_data -->
                    <div
                      v-else-if="attribute.type === 'text'"
                      class="info-value"
                    >
                      {{ lang(attribute.valueLang) }}
                    </div>
                    <div
                      v-else-if="attribute.type === 'textarea'"
                      class="info-value"
                    >
                      {{ lang(attribute.valueLang) }}
                    </div>
                    <div
                      v-else-if="attribute.type === 'wysiwyg'"
                      class="info-value"
                      v-html="lang(attribute.valueLang)"
                    ></div>
                  </div>
                </template>

                <!-- Special components (fullname, email, price) -->
                <template
                  v-else-if="
                    attribute.kind === 'component' && canDisplay(attribute)
                  "
                >
                  <div class="input-group">
                    <label v-show="showComponentLabel(attribute)" class="label">
                      <span :class="labelVisibilityClasses(attribute)">
                        {{ compLabel(attribute) }}
                        <template v-if="getAttributeRequiredFlag(attribute)">
                          <span class="required-symbol">*</span>
                        </template>
                      </span>
                    </label>

                    <template v-if="attribute.type === 'tender_form_fullname'">
                      <input
                        :id="attribute.identifier"
                        v-model="fullName"
                        :name="attribute.identifier"
                        class="input--style-ep"
                        type="text"
                        disabled
                      />
                    </template>

                    <template
                      v-else-if="attribute.type === 'tender_form_email'"
                    >
                      <input
                        :id="attribute.identifier"
                        v-model="email"
                        :name="attribute.identifier"
                        class="input--style-ep"
                        type="text"
                        disabled
                      />
                    </template>

                    <div v-else-if="attribute.type === 'tender_form_price'">
                      <div class="row">
                        <div class="col-lg-12 price-input-inline">
                          <input
                            :id="attribute.identifier"
                            v-model.number="
                              model.form_data[attribute.identifier]
                            "
                            :name="attribute.identifier"
                            type="number"
                            class="input--style-ep"
                            step="0.01"
                            inputmode="decimal"
                            @input="setOfferedPrice($event)"
                            @change="setOfferedPrice($event)"
                          />
                          <span class="price-currency"> / EUR</span>
                        </div>
                      </div>
                      <div
                        v-if="state('form_data.' + attribute.identifier)"
                        class="form-validation row"
                        :class="state('form_data.' + attribute.identifier)"
                      >
                        <div class="col-lg-12">
                          <b-form-feedback
                            v-html="
                              feedback('form_data.' + attribute.identifier)
                            "
                          />
                        </div>
                      </div>
                    </div>

                    <div
                      v-else-if="
                        attribute.type === 'tender_form_participation_fee'
                      "
                    >
                      <div class="row">
                        <div class="col-lg-12 price-input-inline">
                          <input
                            :id="attribute.identifier"
                            v-model.number="
                              model.form_data[attribute.identifier]
                            "
                            :name="attribute.identifier"
                            type="number"
                            class="input--style-ep"
                            step="0.01"
                            inputmode="decimal"
                            @input="setParticipationFee($event)"
                            @change="setParticipationFee($event)"
                          />
                          <span class="price-currency"> / EUR</span>
                        </div>
                      </div>
                      <div
                        v-if="state('form_data.' + attribute.identifier)"
                        class="form-validation row"
                        :class="state('form_data.' + attribute.identifier)"
                      >
                        <div class="col-lg-12">
                          <b-form-feedback
                            v-html="
                              feedback('form_data.' + attribute.identifier)
                            "
                          />
                        </div>
                      </div>
                    </div>

                    <template
                      v-else-if="
                        attribute.type === 'tender_form_email_notify' &&
                        isAltEmailEnabled()
                      "
                    >
                      <input
                        :id="attribute.identifier"
                        v-model="model.form_data[attribute.identifier]"
                        :name="attribute.identifier"
                        type="text"
                        class="input--style-ep"
                        :placeholder="lang(attribute.placeholder)"
                        autocomplete="email"
                      />
                      <div
                        v-if="state('form_data.' + attribute.identifier)"
                        class="form-validation row"
                        :class="state('form_data.' + attribute.identifier)"
                      >
                        <div class="col-lg-12">
                          <b-form-feedback
                            v-html="
                              feedback('form_data.' + attribute.identifier)
                            "
                          />
                        </div>
                      </div>
                    </template>

                    <!-- Display-only component types -->
                    <template v-else-if="attribute.type === 'tender_purpose'">
                      <div
                        class="info-value"
                        v-html="lang(tender.purpose_content)"
                      ></div>
                    </template>

                    <template
                      v-else-if="attribute.type === 'tender_description'"
                    >
                      <div
                        class="alert alert-secondary tender-description"
                        v-html="lang(tender.description)"
                      ></div>
                    </template>

                    <template v-else-if="attribute.type === 'minimum_fee'">
                      <div>
                        <div class="tp-fee text-left">
                          {{ tender.minimum_fee }} EUR
                        </div>
                        <div class="tp-minfee-words text-muted text-left">
                          {{ tender.minimum_fee_words }}
                        </div>
                      </div>
                    </template>

                    <template
                      v-else-if="attribute.type === 'participation_fee'"
                    >
                      <div>
                        <div class="tp-fee text-left">
                          {{ tender.participation_fee }} EUR
                        </div>
                        <div class="tp-minfee-words text-muted text-left">
                          {{ tender.participation_fee_words }}
                        </div>
                      </div>
                    </template>
                  </div>
                </template>

                <!-- Regular form elements -->
                <template v-else>
                  <template
                    v-if="
                      attribute.kind === 'form_element' && canDisplay(attribute)
                    "
                  >
                    <div class="input-group">
                      <label v-if="attribute.type !== 'toggle'" class="label">
                        <span :class="labelVisibilityClasses(attribute)">
                          <template v-if="attribute.type !== 'toggle'">
                            <template
                              v-if="attribute.type === 'tender_purpose'"
                            >
                              {{ lang(tender.purpose_label) }}
                            </template>
                            <template v-else>
                              {{ lang(attribute.name) }}
                            </template>
                          </template>
                          <template
                            v-if="
                              getAttributeRequiredFlag(attribute) &&
                              attribute.type !== 'toggle'
                            "
                          >
                            <span class="required-symbol">*</span>
                          </template>
                        </span>
                      </label>

                      <!-- Special display-only fields coming from tender object -->
                      <template v-if="attribute.type === 'tender_purpose'">
                        <div
                          class="info-value"
                          v-html="lang(tender.purpose_content)"
                        ></div>
                      </template>

                      <template
                        v-else-if="attribute.type === 'tender_description'"
                      >
                        <div
                          class="alert alert-secondary tender-description"
                          v-html="lang(tender.description)"
                        ></div>
                      </template>

                      <template v-else-if="attribute.type === 'minimum_fee'">
                        <div>
                          <div class="tp-fee text-left">
                            {{ tender.minimum_fee }} EUR
                          </div>
                          <div class="tp-minfee-words text-muted text-left">
                            {{ tender.minimum_fee_words }}
                          </div>
                        </div>
                      </template>

                      <!-- Regular interactive fields -->
                      <template v-else-if="attribute.type === 'text'">
                        <input
                          :id="attribute.identifier"
                          v-model="model.form_data[attribute.identifier]"
                          :name="attribute.identifier"
                          class="input--style-ep"
                          type="text"
                          :placeholder="lang(attribute.placeholder)"
                        />
                      </template>

                      <template v-else-if="attribute.type === 'textarea'">
                        <textarea
                          :id="attribute.identifier"
                          v-model="model.form_data[attribute.identifier]"
                          :name="attribute.identifier"
                          class="input--style-ep"
                          rows="4"
                          :placeholder="lang(attribute.placeholder)"
                        />
                      </template>

                      <template v-else-if="attribute.type === 'wysiwyg'">
                        <rich-text-editor
                          :id="attribute.identifier"
                          v-model="model.form_data[attribute.identifier]"
                          :name="attribute.identifier"
                          class="input--style-ep"
                        />
                      </template>

                      <template v-else-if="attribute.type === 'email'">
                        <input
                          :id="attribute.identifier"
                          v-model="model.form_data[attribute.identifier]"
                          :name="attribute.identifier"
                          type="email"
                          class="input--style-ep"
                          :placeholder="lang(attribute.placeholder)"
                        />
                      </template>

                      <template v-else-if="attribute.type === 'number'">
                        <input
                          :id="attribute.identifier"
                          v-model.number="model.form_data[attribute.identifier]"
                          :name="attribute.identifier"
                          type="number"
                          class="input--style-ep"
                          :placeholder="lang(attribute.placeholder)"
                        />
                      </template>

                      <template v-else-if="attribute.type === 'select'">
                        <div
                          class="rs-select2 js-select-simple select--no-search"
                        >
                          <b-form-select
                            :id="attribute.identifier"
                            v-model="model.form_data[attribute.identifier]"
                            :name="attribute.identifier"
                            class="form-control form-control-format select-box-with-arrow"
                          >
                            <template #first>
                              <b-form-select-option
                                :value="
                                  _.isEmpty(
                                    model.form_data[attribute.identifier],
                                  )
                                    ? model.form_data[attribute.identifier]
                                    : null
                                "
                              >
                                {{
                                  lang(attribute.placeholder) ||
                                  $t('services.label.please-select')
                                }}
                              </b-form-select-option>
                            </template>
                            <b-form-select-option
                              v-for="(option, optionIdx) in attribute.options"
                              :key="optionIdx"
                              :value="option.id"
                            >
                              {{ option.text }}
                            </b-form-select-option>
                          </b-form-select>
                        </div>
                      </template>

                      <template v-else-if="attribute.type === 'checkbox'">
                        <b-form-checkbox-group
                          v-model="model.form_data[attribute.identifier]"
                          stacked
                          :name="`${attribute.identifier}[]`"
                        >
                          <b-form-checkbox
                            v-for="option in attribute.options"
                            :key="option.value"
                            v-b-tooltip.left
                            :value="option.value"
                          >
                            {{ option.text }}
                          </b-form-checkbox>
                        </b-form-checkbox-group>
                      </template>

                      <template v-else-if="attribute.type === 'radio'">
                        <b-form-radio-group
                          :id="attribute.identifier"
                          v-model="model.form_data[attribute.identifier]"
                          :options="radioOptions(attribute)"
                          :name="attribute.identifier"
                          stacked
                        />
                      </template>

                      <template v-else-if="attribute.type === 'upload'">
                        <FileUpload
                          module="tenders"
                          :identifier="attribute.identifier"
                          :unique-folder-id="
                            model.form_data[attribute.identifier]
                          "
                          :multiple="isFileUploadMultiple(attribute)"
                          :max-files="maxFilesFor(attribute)"
                          :deletable-on-create="true"
                          :deletable-on-edit="true"
                        />
                      </template>

                      <template v-else-if="attribute.type === 'toggle'">
                        <div class="toggle-wrap">
                          <template v-if="getAttributeRequiredFlag(attribute)">
                            <span class="required-symbol">*</span>
                          </template>
                          <c-switch
                            v-model="model.form_data[attribute.identifier]"
                            :name="attribute.identifier"
                            :description="lang(attribute.name)"
                            :class="
                              getAttributeRequiredFlag(attribute)
                                ? 'required-switch'
                                : ''
                            "
                          />
                        </div>
                      </template>

                      <small
                        v-if="lang(attribute.note)"
                        tabindex="-1"
                        class="form-text text-muted field-notes"
                      >
                        {{ lang(attribute.note) }}
                      </small>

                      <div
                        v-if="state('form_data.' + attribute.identifier)"
                        class="form-validation row"
                        :class="state('form_data.' + attribute.identifier)"
                      >
                        <div class="col-lg-12">
                          <b-form-feedback
                            v-html="
                              feedback('form_data.' + attribute.identifier)
                            "
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </div>

            <!-- Submit -->
            <div class="row row-service-form btn-row-wrap">
              <div class="col-xs-12 col-md-6 col-value">
                <a
                  :href="`/${tender.tender_url}`"
                  class="btn btn-primary blue-light-fill-in"
                >
                  <font-awesome-icon icon="chevron-left" /> Atgal į konkursą
                </a>
              </div>
              <div
                class="col-xs-12 col-md-6 col-value"
                style="text-align: right"
              >
                <button
                  class="btn btn-primary blue btn-full-width"
                  type="submit"
                  :disabled="isUploading || pending"
                  :aria-busy="isUploading ? 'true' : 'false'"
                >
                  <span v-if="isUploading">Įkeliami failai…</span>
                  <span v-else>
                    Toliau <font-awesome-icon icon="chevron-right" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import form from '@core/js/mixins/form'
import RichTextEditor from '@core/js/components/Plugins/RichTextEditor'
import FileUpload from '@assets/js/components/Upload/FileUpload'
import FileFullPreviewMode from '@assets/js/components/Preview/FileFullPreviewMode'
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {
  name: 'ServiceForm',
  components: { RichTextEditor, FileFullPreviewMode, FileUpload },
  mixins: [form],
  props: {
    tender: { type: Object, default: () => ({}) },
    data: { type: Object, default: () => ({}) },
    fullName: { type: String, default: '' },
    email: { type: String, default: '' },
    mode: { type: String, default: '' },
    id: { type: [String, Number], default: '' },
    // Prefer this prop if provided (passed from Blade)
    isJuridical: { type: [Boolean, Number, String], default: null },
  },
  data() {
    return {
      modelName: 'formRecord',
      resourceRoute: 'formRecords',
      routePath: '/account/tenders/create',
      createPath: '/account/tenders/create',
      updatePath: '/account/tenders/update',
      continuePath: '/account/tenders',
      isBusy: false,
      // eslint-disable-next-line vue/no-reserved-keys
      _priceIdentifierCache: null, // remember last price field identifier
      // eslint-disable-next-line vue/no-reserved-keys
      _,
      // eslint-disable-next-line vue/no-reserved-keys
      _altEmailToggleId: null,
    }
  },
  computed: {
    ...mapGetters(['filesInQueueTotal', 'isProcessingFile']),
    isUploading() {
      return this.filesInQueueTotal > 0 || this.isProcessingFile
    },
    model() {
      return this.$store.getters['tenderForm/data']
    },
    formSchema() {
      return this.tender && this.tender.data_form ? this.tender.data_form : []
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$elementEqualHeight('.col-service-form label')
    })
  },
  beforeMount() {
    if (this.mode === 'create') {
      // legacy-safe mutations (aliases exist in the refactored store)
      this.$store.commit('tenderForm/setDataInit', this.tender)
      this.$store.commit('tenderForm/emptyFields')
      this.$set(this.model, 'id', undefined)
    } else {
      this.$store.commit('tenderForm/setData', this.data)
    }
  },
  methods: {
    // Validate that required file uploads have files
    async validateFileUploads() {
      const formData = this.model.form_data
      const uploadFields = this.formSchema.filter(
        (attr) => attr.type === 'upload',
      )

      for (const field of uploadFields) {
        const isRequired = this.getAttributeRequiredFlag(field)
        const identifier = field.identifier
        const folderUid = formData[identifier]

        if (isRequired) {
          /*          if (!folderUid) {
            throw new Error(
              `Required file upload field "${field.name}" is missing files.`,
            )
          }*/

          // Check if the folder actually has files
          try {
            const response = await this.$store.dispatch('getFolderById', {
              folder_uid: folderUid,
            })

            /*            if (
              !response.data ||
              !Array.isArray(response.data) ||
              response.data.length === 0
            ) {
              throw new Error(
                `Required file upload field "${this.lang(field.name) || identifier}" has no files.`,
              )
            }*/

            /*            console.log(
              `✓ Required field ${identifier} has ${response.data.length} files`,
            )*/
          } catch (error) {
            /*            console.error(`Failed to validate files for ${identifier}:`, error)
            throw new Error(
              `Required file upload field "${this.lang(field.name) || identifier}" validation failed.`,
            )*/
          }
        }
      }
    },

    // Build radio options; default to Taip/Ne when none provided
    radioOptions(attribute) {
      const opts = attribute && attribute.options
      if (!opts || opts === 'undefined') {
        return [
          { value: 1, text: 'Taip' },
          { value: 0, text: 'Ne' },
        ]
      }
      if (Array.isArray(opts)) return opts
      if (typeof opts === 'string') {
        try {
          const parsed = JSON.parse(opts)
          return Array.isArray(parsed)
            ? parsed
            : [
                { value: 1, text: 'Taip' },
                { value: 0, text: 'Ne' },
              ]
        } catch (e) {
          return [
            { value: 1, text: 'Taip' },
            { value: 0, text: 'Ne' },
          ]
        }
      }
      return [
        { value: 1, text: 'Taip' },
        { value: 0, text: 'Ne' },
      ]
    },

    // Component label resolver
    compLabel(attribute) {
      const t = attribute && attribute.type
      if (t === 'tender_form_fullname') return 'Pareiškėjas'
      if (t === 'tender_form_email') return 'El. pašto adresas'
      if (t === 'tender_purpose')
        return this.lang(this.tender && this.tender.purpose_label)
      // default to provided name
      return this.lang(attribute && attribute.name)
    },

    // Safely resolve localized field values

    // Safely resolve localized field values
    lang(obj, fallback = '') {
      if (!obj || typeof obj !== 'object') return fallback
      const cur = this.$app?.lang || 'en'
      return obj[cur] ?? obj.en ?? obj.lt ?? fallback
    },
    columnClass(attribute) {
      const m =
        attribute.layout && attribute.layout.m_col ? attribute.layout.m_col : 12
      const t =
        attribute.layout && attribute.layout.t_col ? attribute.layout.t_col : 12
      const d =
        attribute.layout && attribute.layout.d_col ? attribute.layout.d_col : 12
      return `col-xs-${m} col-sm-${t} col-md-${d}`
    },
    labelVisibilityClasses(attribute) {
      const m =
        attribute.layout && attribute.layout.m_n === '0' ? 'hide' : 'show'
      const t =
        attribute.layout && attribute.layout.t_n === '0' ? 'hide' : 'show'
      const d =
        attribute.layout && attribute.layout.d_n === '0' ? 'hide' : 'show'
      return `label-content label-xs-${m} label-sm-${t} label-md-${d}`
    },
    canDisplay(attribute) {
      // Conditional visibility rules
      const key = attribute?.key || ''
      const type = attribute?.type || ''
      // Show these only for juridical users
      if (
        key === 'certificate_from_sodra' ||
        type === 'certificate_from_sodra' ||
        key === 'certificate_from_vmi' ||
        type === 'certificate_from_vmi'
      ) {
        return this.isJuridicalUser()
      }
      // Show this only for natural persons (non-juridical)
      if (key === 'personal_id_copy' || type === 'personal_id_copy') {
        return !this.isJuridicalUser()
      }
      // Default: visible
      return true
    },

    isJuridicalUser() {
      // 1) Prefer explicit prop from Blade
      let v = this.isJuridical
      // 2) Fallback to Vuex userAuth module if prop not provided
      if (v === null || v === undefined) {
        const user = this.$store?.getters?.['userAuth/user']
        v = user?.is_juridical ?? user?.juridical ?? user?.isJuridical
      }
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase()
        if (s === '1' || s === 'true' || s === 'yes' || s === 'taip')
          return true
        if (s === '0' || s === 'false' || s === 'no' || s === 'ne') return false
      }
      if (typeof v === 'number') return v === 1
      if (typeof v === 'boolean') return v
      // Fallback: not juridical
      return false
    },
    setOfferedPrice(event) {
      const raw = event?.target?.value
      let num = raw === '' || raw == null ? null : Number(raw)

      // Enforce minimum offer not less than tender.minimum_fee (if provided)
      //const minFee = Number(this.tender?.minimum_fee)
      const min = 0.01
      if (Number.isFinite(num) && num < min) {
        num = min
        if (event && event.target) event.target.value = String(min)
      }

      this.$store.commit(
        'tenderForm/setOfferedPrice',
        Number.isFinite(num) ? num : null,
      )

      // also remember which identifier carries price and keep it mirrored in form_data
      const id = event?.target?.name || event?.target?.id
      if (id) this._priceIdentifierCache = id
      if (this._priceIdentifierCache) {
        this.$set(
          this.model.form_data,
          this._priceIdentifierCache,
          Number.isFinite(num) ? num : null,
        )
      }
    },
    isAltEmailEnabled() {
      // Find and cache the identifier for the notify_use_alt_email radio
      if (!this._altEmailToggleId) {
        const toggleAttr = (this.formSchema || []).find(
          (a) =>
            a && a.kind === 'form_element' && a.key === 'notify_use_alt_email',
        )
        if (toggleAttr && toggleAttr.identifier) {
          this._altEmailToggleId = toggleAttr.identifier
        }
      }
      const id = this._altEmailToggleId
      let v = id ? this.model?.form_data?.[id] : undefined
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase()
        if (s === 'yes' || s === 'taip' || s === '1' || s === 'true')
          return true
        if (s === 'no' || s === 'ne' || s === '0' || s === 'false') return false
      }
      return v === 1 || v === true
    },

    // Show label for components; hide email notify label when alt email is disabled
    showComponentLabel(attribute) {
      if (!attribute) return true
      if (attribute.type === 'tender_form_email_notify') {
        return this.isAltEmailEnabled()
      }
      return true
    },
    setParticipationFee(event) {
      const identifier = event?.target?.name || event?.target?.id
      const raw = event?.target?.value
      let value =
        raw === '' || raw === null || raw === undefined ? null : Number(raw)
      if (!Number.isFinite(value)) value = null
      this.$store.commit('tenderForm/setPaidParticipationFee', value)
      if (identifier) {
        this.$set(this.model.form_data, identifier, value)
      }
    },

    getAttributeRequiredFlag(param) {
      const required = param?.validation?.required
      return (
        required === 1 ||
        required === true ||
        required === '1' ||
        required === 'true'
      )
    },

    // Determine if file upload should allow multiple files
    isFileUploadMultiple(attribute) {
      // These specific fields should only allow single file upload
      const singleFileKeys = [
        'certificate_from_sodra',
        'certificate_from_vmi',
        'personal_id_copy',
      ]

      const key = attribute?.key
      if (key && singleFileKeys.includes(key)) {
        return false
      }

      // Default: allow multiple files
      return true
    },
    maxFilesFor(attribute) {
      const key = attribute?.key || attribute?.type
      if (key === 'payment_receipt') {
        return 2
      }
      return null
    },

    // Override the onSubmit method from form mixin to collect files
    async onSubmit(event) {
      this.pending = true

      try {
        // Before submission, validate file uploads
        await this.validateFileUploads()

        const action = this.isNew
          ? this.createPath
          : [this.updatePath, this.id].filter(Boolean).join('/')

        // Convert model to FormData
        const formData = this.$app.objectToFormData(this.model)

        if (!this.isNew) {
          formData.append('_method', 'PUT')
        }

        const { data } = await axios.post(action, formData)

        this.pending = false

        if (this.isModal) {
          this.$emit('context-changed')
        }

        // Navigate back to list if configured and no continuePath
        if (this.listPath && !this.continuePath) {
          window.location.replace(this.listPath)
        }

        // Continue flow
        if (this.continuePath && data && data.id) {
          window.location.replace(
            `${this.continuePath}/${data.id}/submit-review`,
          )
        }

        // If modal, refresh and close
        if (this.isModal) {
          this.refreshData?.()
          this.$emit('close')
        }
      } catch (e) {
        this.pending = false

        // Handle our custom file validation errors
        if (e.message && e.message.includes('Required file upload field')) {
          // Show a user-friendly error message
          if (this.$app.error) {
            this.$app.error(e.message)
          } else {
            alert(e.message)
          }
          return
        }

        // Validation errors
        if (e?.response?.status === 422) {
          this.validation = e.response.data || {}

          // Scroll to first invalid field
          const options = {
            container: 'body',
            easing: 'ease-in-out',
            offset: -150,
            force: true,
            cancelable: true,
            onStart: () => {},
            onDone: () => {},
            onCancel: () => {},
            x: false,
            y: true,
          }

          this.$nextTick(() => {
            const firstInvalid = document.querySelector('.invalid')
            if (firstInvalid) {
              // If you use vue-scrollto plugin:
              if (typeof this.$scrollTo === 'function') {
                this.$scrollTo(firstInvalid, 400, options)
              } else {
                // native smooth scroll fallback
                const top =
                  firstInvalid.getBoundingClientRect().top +
                  window.pageYOffset +
                  (options.offset || 0)
                window.scrollTo({ top, behavior: 'smooth' })
              }
            }
          })

          return
        }

        // Other errors
        this.$app.error?.(e)
      }
    },
  },
}
</script>
<style scoped>
.price-input-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.price-currency {
  white-space: nowrap;
}
[v-cloak] {
  display: none;
}
:deep(.card) {
  color: unset;
}
.tp-fee {
  padding: 2px 0 5px 0;
  font-weight: 600;
  color: #6da4cb;
  text-align: left;
  font-size: 16px;
}
.info-label {
  padding: 0;
  font-weight: 400;
  color: #4b4b4b;
  font-size: 16px;
}
</style>
